import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { businessApi } from '../services/api';
import { Business } from '../types';
import {
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';


export function BusinessDetail() {
  const { id } = useParams<{ id: string }>();
  const [business, setBusiness] = useState<Business | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchBusinessData();
    }
  }, [id]);

  const fetchBusinessData = async () => {
    try {
      const businessRes = await businessApi.getBusiness(id!);
      setBusiness(businessRes.data.data.business);
    } catch (error) {
      toast.error('Failed to load business data');
    } finally {
      setIsLoading(false);
    }
  };


  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="card p-8 text-center">
        <p className="text-secondary-500">Business not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">{business.name}</h1>
          <p className="text-secondary-500">{business.description || 'No description'}</p>
        </div>
        <span
          className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
            business.isActive
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          {business.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      {/* Contact Info */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Contact Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {business.address && (
            <div className="flex items-center text-secondary-600">
              <MapPinIcon className="h-5 w-5 mr-3 text-secondary-400" />
              {business.address}
            </div>
          )}
          {business.phone && (
            <div className="flex items-center text-secondary-600">
              <PhoneIcon className="h-5 w-5 mr-3 text-secondary-400" />
              {business.phone}
            </div>
          )}
          {business.email && (
            <div className="flex items-center text-secondary-600">
              <EnvelopeIcon className="h-5 w-5 mr-3 text-secondary-400" />
              {business.email}
            </div>
          )}
          {business.website && (
            <div className="flex items-center text-secondary-600">
              <GlobeAltIcon className="h-5 w-5 mr-3 text-secondary-400" />
              <a
                href={business.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-500"
              >
                {business.website}
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      
      {/* Quick Links */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Manage</h3>
        <div className="flex flex-wrap gap-3">
          <a href={`/services?businessId=${business.id}`} className="btn-primary">
            View Services
          </a>
          <a href={`/staff?businessId=${business.id}`} className="btn-secondary">
            Manage Staff
          </a>
          <a href={`/bookings?businessId=${business.id}`} className="btn-secondary">
            View Bookings
          </a>
          <a href={`/events?businessId=${business.id}`} className="btn-secondary">
            Manage Events
          </a>
        </div>
      </div>
    </div>
  );
}
